import { Explain } from '@/components/Explain';
import { STATUS_META } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { SourceStatus } from '@/types';
import { SOURCES, countOf } from '@/features/bet-inspection/components/SourceRail';
import type { BetSide } from '../lib/side';
import { LaneLetter } from './CompareSummary';

/**
 * Per-source availability, A vs B. A source that answered for one bet but not the
 * other is flagged — "A has realtime rows, B's realtime is empty" is exactly the
 * kind of copy-pipeline gap this tool exists to surface, and comparing to a
 * known-good bet makes it obvious.
 */
export function CompareSources({ a, b }: { a: BetSide; b: BetSide }) {
  const rows = SOURCES.map((s) => {
    const sa = a.bundle[s.key].status;
    const sb = b.bundle[s.key].status;
    return { ...s, sa, sb, ca: countOf(a.bundle, s.key), cb: countOf(b.bundle, s.key), flagged: sa !== sb };
  });
  const diffCount = rows.filter((r) => r.flagged).length;

  return (
    <section className="border-b border-hairline">
      <div className="flex items-center gap-3 px-4 py-2">
        <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
          Sources
        </span>
        <span className={cn('font-mono text-[11px]', diffCount ? 'text-warn' : 'text-text-dim')}>
          {diffCount ? `${diffCount} differ` : 'same availability'}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr className="border-b border-hairline">
              <th className="w-[150px] px-4 py-2 text-left eyebrow">source</th>
              <th className="px-4 py-2 text-left">
                <LaneLetter letter="A" />
              </th>
              <th className="px-4 py-2 text-left">
                <LaneLetter letter="B" />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                className={cn('border-b border-hairline/60', r.flagged && 'bg-warn-bg/25')}
              >
                <td className="px-4 py-1.5">
                  <span className="text-[12px] text-text-dim">
                    <Explain term={r.glossary}>{r.label}</Explain>
                  </span>
                </td>
                <StatusCell status={r.sa} count={r.ca} />
                <StatusCell status={r.sb} count={r.cb} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StatusCell({ status, count }: { status: SourceStatus; count: number }) {
  const meta = STATUS_META[status];
  return (
    <td className="px-4 py-1.5">
      <span className="inline-flex items-center gap-1.5 font-mono text-[12px]">
        <span className={meta.color}>{meta.glyph}</span>
        <span className="text-text-dim">{meta.label}</span>
        {status === 'ok' && count > 0 && <span className="tnum text-text-faint">{count}</span>}
      </span>
    </td>
  );
}
